import {
  Children,
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { AircraftData, MaoAirport } from '#entities';

type SelectedAirport = Pick<MaoAirport, 'IanaTimeZone' | 'ICAO' | 'Id'>;

export interface AircraftDrawerContextProps {
  aircraftData: AircraftData;
  selectedAirport?: SelectedAirport;
  selectAirport: (airport?: SelectedAirport) => void;
}

export const AircraftDrawerContext = createContext<
  AircraftDrawerContextProps | undefined
>(undefined);

export const useAircraftDrawer = (): AircraftDrawerContextProps => {
  const context = useContext(AircraftDrawerContext);
  if (!context) {
    throw new Error(
      'useAircraftDrawer must be used within an AircraftDrawerProvider',
    );
  }
  return context;
};

export const AircraftDrawerProvider = (
  props: {
    children:
      | ((
          props: AircraftData,
          ctx: AircraftDrawerContextProps,
        ) => React.ReactNode)
      | React.ReactNode;
    open?: boolean;
  } & Pick<AircraftDrawerContextProps, 'aircraftData' | 'selectedAirport'>,
) => {
  const [selectedAirport, selectAirport] = useState<
    SelectedAirport | undefined
  >(props.selectedAirport);

  // Re-seed the default airport every time the drawer opens. The drawer stays
  // mounted with `keepMounted`, so closing it (which clears `selectedAirport`)
  // and reopening the SAME aircraft would otherwise never re-set the airport —
  // the child `AircraftDrawerLocation` effect re-runs only when `airportLastKnown`
  // changes (i.e. a different aircraft), not on reopen of the same one — leaving
  // METAR/TAF blank on the second open (TSMED-1088). Done during render via the
  // "store previous value" pattern rather than an effect, so the reset is applied
  // before paint (no post-commit flash of empty weather).
  const open = Boolean(props.open);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      selectAirport(props.selectedAirport);
    }
  }

  const value: AircraftDrawerContextProps = useMemo(
    () => ({
      aircraftData: props.aircraftData,
      selectedAirport,
      selectAirport,
    }),
    [props.aircraftData, selectedAirport],
  );
  return (
    <AircraftDrawerContext.Provider value={value}>
      {props.aircraftData
        ? typeof props.children === 'function'
          ? (
              props.children as (
                props: AircraftData,
                ctx: AircraftDrawerContextProps,
              ) => ReactNode
            )(props.aircraftData, value)
          : Children.count(props.children) !== 0
            ? Children.only(props.children)
            : null
        : null}
    </AircraftDrawerContext.Provider>
  );
};
